import React, { useState, useEffect } from 'react';
import { getCustomers, searchCustomerByPhone, createCustomer, Customer, CustomerType, PricingTier } from '../../services/customerService';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { getPricingTierBadge } from '../../utils/pricingTier';

// Virtual retail customer (not stored in database)
// Used for walk-in sales without specific customer data
const VIRTUAL_RETAIL_CUSTOMER: Customer = {
  id: 0, // Virtual ID (not in database)
  name: "Walk-in Customer",
  phone: "NON-MEMBER",
  type: CustomerType.END_USER,
  pricing_tier: PricingTier.RETAIL_MARKUP, // +35%
  branch_id: 0,
  notes: "Retail customer with 35% markup",
  is_active: true,
  created_at: new Date().toISOString(),
};

interface CustomerSelectorProps {
  selectedCustomerId: number | null;
  onCustomerSelect: (customerId: number | null, customer: Customer | null) => void;
  darkMode?: boolean;
}

const CustomerSelector: React.FC<CustomerSelectorProps> = ({
  selectedCustomerId,
  onCustomerSelect,
  darkMode = false,
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchPhone, setSearchPhone] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    type: CustomerType.END_USER,
  });

  const currentUser = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    loadRecentCustomers();
  }, []);

  const loadRecentCustomers = async () => {
    try {
      const data = await getCustomers({ limit: 10, offset: 0 });
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const handleSearchByPhone = async () => {
    if (!searchPhone.trim()) return;

    setLoading(true);
    try {
      const results = await searchCustomerByPhone(searchPhone.trim());
      if (results.length > 0) {
        // Found customer(s), select the first one
        onCustomerSelect(results[0].id, results[0]);
        setSearchPhone('');
      } else {
        // Customer not found, show create form
        setNewCustomer({ ...newCustomer, phone: searchPhone.trim() });
        setShowCreateForm(true);
      }
    } catch (error) {
      // Error or not found, show create form
      setNewCustomer({ ...newCustomer, phone: searchPhone.trim() });
      setShowCreateForm(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser?.branch_id) {
      alert('Branch ID tidak ditemukan');
      return;
    }

    setLoading(true);
    try {
      const created = await createCustomer({
        ...newCustomer,
        branch_id: currentUser.branch_id,
      });
      
      onCustomerSelect(created.id, created);
      setCustomers([created, ...customers]);
      setShowCreateForm(false);
      setNewCustomer({
        name: '',
        phone: '',
        type: CustomerType.END_USER,
      });
      setSearchPhone('');
      alert('Customer berhasil dibuat!');
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Gagal membuat customer');
    } finally {
      setLoading(false);
    }
  };

  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);

  return (
    <div className="mb-0">
      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        👤 Customer (Opsional)
      </label>
      
      {/* Selected Customer Display */}
      {selectedCustomer ? (
        <div className={`rounded p-3 mb-2 border ${
          darkMode 
            ? 'bg-blue-900/30 border-blue-700' 
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex justify-between items-start">
            <div>
              <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {selectedCustomer.name}
              </div>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {selectedCustomer.phone}
              </div>
              <div className="text-xs mt-1">
                <span className={`px-2 py-0.5 rounded ${
                  selectedCustomer.type === CustomerType.TECHNICIAN
                    ? darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                    : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                }`}>
                  {selectedCustomer.type === CustomerType.TECHNICIAN ? 'Teknisi' : 'End User'}
                </span>
                {' '}
                <span className={getPricingTierBadge(selectedCustomer.pricing_tier).className}>
                  {getPricingTierBadge(selectedCustomer.pricing_tier).label}
                </span>
              </div>
            </div>
            <button
              onClick={() => onCustomerSelect(null, null)}
              className={`${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'}`}
            >
              ✕
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Search by Phone */}
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={searchPhone}
              onChange={(e) => setSearchPhone(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchByPhone()}
              placeholder="Cari by nomor HP..."
              className={`flex-1 px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'border border-gray-300 text-gray-900'
              }`}
            />
            <button
              onClick={handleSearchByPhone}
              disabled={loading || !searchPhone.trim()}
              className={`px-4 py-2 rounded disabled:opacity-50 ${
                darkMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {loading ? '...' : 'Cari'}
            </button>
          </div>

          {/* Quick Select from Recent */}
          <div className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Atau pilih customer recent:
          </div>
          <select
            value={selectedCustomerId === 0 ? 'RETAIL' : (selectedCustomerId || '')}
            onChange={(e) => {
              const value = e.target.value;
              
              // Handle "Tanpa Customer" selection (virtual retail)
              if (value === 'RETAIL') {
                onCustomerSelect(VIRTUAL_RETAIL_CUSTOMER.id, VIRTUAL_RETAIL_CUSTOMER);
                return;
              }
              
              const customerId = value ? parseInt(value) : null;
              const customer = customers.find(c => c.id === customerId) || null;
              onCustomerSelect(customerId, customer);
            }}
            className={`w-full px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'border border-gray-300 text-gray-900'
            }`}
          >
            <option value="RETAIL">
              🔥 Tanpa Customer (Retail +35%)
            </option>
            <option disabled>─────────────────</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name} - {customer.phone}
              </option>
            ))}
          </select>
        </>
      )}

      {/* Create Customer Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4">Customer Baru</h3>
            <form onSubmit={handleCreateCustomer} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nama *</label>
                <input
                  type="text"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="Nama customer"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">No. HP *</label>
                <input
                  type="text"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                  placeholder="08xxxxxxxxxx"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Tipe Customer</label>
                <select
                  value={newCustomer.type}
                  onChange={(e) => setNewCustomer({ ...newCustomer, type: e.target.value as CustomerType })}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                >
                  <option value={CustomerType.END_USER}>End User (No Warranty)</option>
                  <option value={CustomerType.TECHNICIAN}>Teknisi (30 Days Warranty)</option>
                </select>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewCustomer({
                      name: '',
                      phone: '',
                      type: CustomerType.END_USER,
                    });
                  }}
                  className="flex-1 px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300"
                >
                  {loading ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSelector;
